import {useCallback, useEffect, useMemo, useState} from 'react';

import {useReadOnlyDataHook} from 'src/hooks/use-read-only-data-hook';
import {BASIC_VALIDATION_FAILED} from 'src/modules/create-account/constants';
import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {Error} from 'src/openapi/admin/models/Error';
import type {CommonHookReturn} from 'src/typedef';

export type SaveContext<Data> = {
    initialData: Data,
    setInitialData: (data: Data) => void,
};

type Props<Data, Result = void, ExtraSaveParams = void> = {
    initValue: Data,
    getDispatchAction: (signal: AbortSignal) => Promise<{
        type: string,
        payload: {
            data: Data,
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
    saveDispatchAction: (
        data: Data,
        signal: AbortSignal,
        extraParams: ExtraSaveParams | undefined,
        context: SaveContext<Data>,
    ) => Promise<{
        type: string,
        payload: {
            data: Result,
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
    editValidation?: (data: Data) => void,
    saveValidation?: (data: Data) => boolean,
    shouldLoadData?: boolean,
};

export const useCommonDataHook: {
    <Data, Result, Extra>(props: Props<Data, Result, Extra>): CommonHookReturn<Data, Result, Extra>,
    <Data, Result>(props: Props<Data, Result, void>): CommonHookReturn<Data, Result, void>,
    <Data, Extra>(props: Props<Data, void, Extra>): CommonHookReturn<Data, void, Extra>,
} = <Data, Result, Extra>(params: Props<Data, Result, Extra>): CommonHookReturn<Data, Result, Extra> => {
    const {
        initValue,
        getDispatchAction,
        saveDispatchAction,
        shouldLoadData = true,
        editValidation,
        saveValidation,
    } = params;

    const {
        data: loadedData,
        isLoading: IsInitialDataLoading,
        loadingError,
        loadData,
        createAbortSignal,
    } = useReadOnlyDataHook({
        getDispatchAction,
        initValue,
        shouldLoadData,
    });

    const [dataInitial, setDataInitial] = useState<Data>(initValue as Data);
    const [data, setData] = useState<Data>(initValue as Data);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setData(loadedData);
        setDataInitial(loadedData);
    }, [loadedData]);

    useEffect(() => {
        setIsLoading(IsInitialDataLoading);
    }, [IsInitialDataLoading]);

    useEffect(() => {
        if (editValidation) {
            editValidation(data);
        }
    }, [data, editValidation]);

    const isChanged = useMemo(() => {
        return !isDeepEqual({current: data, prev: dataInitial});
    }, [data, dataInitial]);

    const validateChanges = useCallback(() => {
        if (saveValidation && !saveValidation(data)) {
            return BASIC_VALIDATION_FAILED;
        } else {
            return null;
        }
    }, [data, saveValidation]);

    const saveChanges = useCallback(async (extraParams?: Extra) => {
        let error: string | null = validateChanges();
        let result: Result | null = null;

        if (error) {
            return error;
        }

        await saveDispatchAction(data, createAbortSignal(), extraParams, {
            initialData: dataInitial,
            setInitialData: setDataInitial,
        }).then(response => {
            if (response.payload.error) {
                error = getError({error: response.payload.data, separator: '\n'});
            } else if (response.payload.success) {
                setDataInitial(data);
                result = response.payload.data;
            }
        });

        if (error) {
            return error;
        } else if (result) {
            return result;
        } else {
            return undefined;
        }
    }, [data, dataInitial, validateChanges, saveDispatchAction, createAbortSignal]);

    const discardChanges = useCallback(() => {
        setData(dataInitial);
        if (editValidation) {
            editValidation(dataInitial);
        }
    }, [dataInitial, editValidation]);

    return {
        data,
        dataInitial,
        saveChanges,
        discardChanges,
        validateChanges,
        isChanged,
        isLoading,
        setData,
        loadData,
        loadingError,
    };
};
